// admin/Dashboard.jsx
// Main admin overview page.
// Shows live stats (total products, orders, users, revenue) and a recent-orders table.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "../../firebase/config";
import { formatCurrency } from "../../utils/pricing";
import { shortOrderId, formatDate, statusBadgeClass } from "../../utils/helpers";

// ── Stat card component ────────────────────────────────────────────────────
function StatCard({ label, value, icon, color, linkTo }) {
  return (
    <Link to={linkTo} className={`bg-white rounded-2xl shadow-sm p-6 flex items-center gap-4 hover:shadow-md transition-shadow`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-extrabold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const [stats, setStats]         = useState({ products: 0, orders: 0, users: 0, revenue: 0 });
  const [recentOrders, setRecent] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        // Fetch all three collections in parallel
        const [productSnap, orderSnap, userSnap] = await Promise.all([
          getDocs(collection(db, "products")),
          getDocs(collection(db, "orders")),
          getDocs(collection(db, "users")),
        ]);

        // Calculate total revenue from all orders
        const totalRevenue = orderSnap.docs.reduce(
          (sum, d) => sum + (d.data().pricing?.total || 0),
          0
        );

        setStats({
          products: productSnap.size,
          orders:   orderSnap.size,
          users:    userSnap.size,
          revenue:  totalRevenue,
        });

        // Recent orders – latest 5
        const recentQ = query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(5));
        const recentSnap = await getDocs(recentQ);
        setRecent(recentSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl h-24" />
          ))}
        </div>
        <div className="bg-white rounded-2xl h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page heading */}
      <div>
        <h1 className="text-2xl font-extrabold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-0.5">Welcome back, here's what's happening in your store.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Products"      value={stats.products} icon="🎁" color="bg-pink-50"   linkTo="/admin/products" />
        <StatCard label="Orders"        value={stats.orders}   icon="📦" color="bg-blue-50"   linkTo="/admin/orders" />
        <StatCard label="Customers"     value={stats.users}    icon="👥" color="bg-green-50"  linkTo="/admin/users" />
        <StatCard label="Total Revenue" value={formatCurrency(stats.revenue)} icon="💰" color="bg-yellow-50" linkTo="/admin/orders" />
      </div>

      {/* Recent orders table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">Recent Orders</h2>
          <Link to="/admin/orders" className="text-sm text-purple-600 hover:underline">View all →</Link>
        </div>

        {recentOrders.length === 0 ? (
          <p className="text-center py-10 text-gray-400">No orders yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-6 py-3">Order ID</th>
                  <th className="text-left px-6 py-3">Customer</th>
                  <th className="text-left px-6 py-3">Date</th>
                  <th className="text-left px-6 py-3">Items</th>
                  <th className="text-right px-6 py-3">Total</th>
                  <th className="text-left px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 font-mono font-semibold text-gray-700">{shortOrderId(order.id)}</td>
                    <td className="px-6 py-3 text-gray-600">{order.shipping?.name || order.userEmail}</td>
                    <td className="px-6 py-3 text-gray-500">{formatDate(order.createdAt)}</td>
                    <td className="px-6 py-3 text-gray-600">{order.items?.length}</td>
                    <td className="px-6 py-3 text-right font-semibold text-gray-800">{formatCurrency(order.pricing?.total)}</td>
                    <td className="px-6 py-3">
                      <span className={`capitalize text-xs font-semibold px-2.5 py-1 rounded-full ${statusBadgeClass(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
